<template>
    <div class="lazy-image-wrapper" :style="{ aspectRatio: aspectRatio }">
        <img :src="currentSrc" :alt="alt" :class="['lazy-image', { 'loaded': isLoaded, 'error': hasError }]"
            @load="handleLoad" @error="handleError" ref="img" />
        <div v-if="!isLoaded && !hasError" class="image-placeholder">
            <div class="loading-spinner"></div>
            <span class="loading-text">加载中...</span>
        </div>
        <div v-if="hasError" class="image-error">
            <span class="error-icon">🖼️</span>
            <span class="error-text">图片加载失败</span>
        </div>
    </div>
</template>

<script>
import configCacheService from '../services/configCacheService'

export default {
    name: 'LazyImage',
    props: {
        src: {
            type: String,
            required: true
        },
        alt: {
            type: String,
            default: ''
        },
        aspectRatio: {
            type: String,
            default: 'auto'
        },
        enableWebP: {
            type: Boolean,
            default: true
        },
        useCache: {
            type: Boolean,
            default: true
        },
        cacheKey: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isLoaded: false,
            hasError: false,
            isIntersecting: false,
            currentSrc: '',
            observer: null,
            webpSupported: false
        }
    },
    computed: {
        finalCacheKey() {
            if (!this.useCache) return null
            return this.cacheKey || configCacheService.generateImageCacheKey(this.src)
        }
    },
    async mounted() {
        // 检测 WebP 支持
        if (this.enableWebP) {
            this.webpSupported = await this.checkWebPSupport()
        }

        // 设置 Intersection Observer
        this.setupIntersectionObserver()
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect()
        }
    },
    methods: {
        checkWebPSupport() {
            return new Promise((resolve) => {
                const webP = new Image()
                webP.onload = webP.onerror = () => {
                    resolve(webP.height === 2)
                }
                webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA'
            })
        },

        setupIntersectionObserver() {
            if ('IntersectionObserver' in window) {
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && !this.isIntersecting) {
                            this.isIntersecting = true
                            this.loadImage()
                            this.observer.disconnect()
                        }
                    })
                }, {
                    rootMargin: '50px'
                })

                this.observer.observe(this.$el)
            } else {
                // 不支持 IntersectionObserver，直接加载
                this.loadImage()
            }
        },

        async loadImage() {
            if (this.useCache && this.finalCacheKey) {
                // 尝试从缓存获取
                const cachedImage = configCacheService.getCachedImage(this.finalCacheKey)
                if (cachedImage) {
                    console.log('✅ LazyImage使用缓存:', this.src)
                    this.currentSrc = cachedImage.url
                    return
                }
            }

            // 获取优化后的图片URL
            this.currentSrc = this.getOptimizedSrc()
        },

        getOptimizedSrc() {
            let src = this.src

            // 如果支持 WebP，尝试转换图片格式
            if (this.webpSupported && this.enableWebP) {
                // 检查是否是常见的图片 URL
                if (this.isRemoteImage(src)) {
                    src = this.convertToWebP(src)
                }
            }

            return src
        },

        isRemoteImage(src) {
            return src.startsWith('http') || src.startsWith('//') || src.includes('unsplash.com') || src.includes('images.')
        },

        convertToWebP(src) {
            // 对于 Unsplash 图片，添加 WebP 格式参数
            if (src.includes('unsplash.com')) {
                const separator = src.includes('?') ? '&' : '?'
                return `${src}${separator}fm=webp&q=80`
            }

            // 对于其他支持 WebP 的 CDN，可以在这里添加转换逻辑
            // 例如：Cloudinary, ImageKit 等

            return src
        },

        handleLoad(event) {
            this.isLoaded = true
            this.hasError = false

            // 缓存图片信息
            if (this.useCache && this.finalCacheKey) {
                const imageInfo = {
                    url: this.src,
                    width: event.target.naturalWidth,
                    height: event.target.naturalHeight,
                    loadTime: Date.now(),
                    element: event.target
                }
                configCacheService.cacheImage(this.finalCacheKey, imageInfo)
                console.log('💾 LazyImage图片已缓存:', this.src)
            }

            this.$emit('load', event)
        },

        handleError() {
            this.hasError = true
            this.isLoaded = false

            // 如果是 WebP 失败，尝试原格式
            if (this.webpSupported && this.currentSrc !== this.src) {
                this.currentSrc = this.src
                return
            }

            this.$emit('error')
        }
    }
}
</script>

<style scoped>
.lazy-image-wrapper {
    position: relative;
    overflow: hidden;
    background: #f5f5f5;
    border-radius: 8px;
}

.lazy-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.3s ease;
    opacity: 0;
}

.lazy-image.loaded {
    opacity: 1;
}

.lazy-image.error {
    opacity: 0;
}

.image-placeholder,
.image-error {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    color: #6c757d;
}

.loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #e9ecef;
    border-top: 2px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 8px;
}

.loading-text,
.error-text {
    font-size: 0.8rem;
    text-align: center;
}

.error-icon {
    font-size: 2rem;
    margin-bottom: 8px;
    opacity: 0.5;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .loading-spinner {
        width: 20px;
        height: 20px;
    }

    .loading-text,
    .error-text {
        font-size: 0.7rem;
    }

    .error-icon {
        font-size: 1.5rem;
    }
}
</style>